import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  
  // Get redirect URL from query params or localStorage
  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect') || localStorage.getItem('auth_redirect');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Por favor, completa todos los campos');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (!acceptTerms) {
      toast.error('Debes aceptar los términos y condiciones');
      return;
    }

    setLoading(true);
    try {
      await register(name, email, password);
      toast.success('¡Cuenta creada con éxito!');
      
      // Clear stored redirect
      localStorage.removeItem('auth_redirect');
      
      // If there's a redirect URL (like checkout), go there instead of onboarding
      if (redirectUrl) {
        navigate(redirectUrl);
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear la cuenta';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1654722439127-0645ad92650b?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Arquitectura"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[hsl(var(--text-primary))]/30" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <blockquote className="text-white text-center">
            <p className="font-accent text-2xl italic mb-4">
              "Aquí no entregas tu poder personal. Aquí lo recuperas."
            </p>
          </blockquote>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Back link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] mb-8 transition-colors"
            data-testid="register-back"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Volver al inicio
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center">
              <span className="text-white font-heading text-lg">A</span>
            </div>
            <span className="font-heading text-xl text-[hsl(var(--text-primary))]">
              Escuela Akáshica
            </span>
          </div>

          <h1 className="text-3xl font-heading text-[hsl(var(--text-primary))] mb-2">
            Crea tu cuenta
          </h1>
          <p className="text-[hsl(var(--text-secondary))] mb-8">
            Comienza tu camino hacia la consciencia habitada
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                type="text"
                placeholder="Tu nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-soft"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-soft"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Mínimo 6 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-soft pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-secondary))]"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" strokeWidth={1.5} />
                  ) : (
                    <Eye className="w-5 h-5" strokeWidth={1.5} />
                  )}
                </button>
              </div>
              <p className="text-xs text-[hsl(var(--text-tertiary))] mt-1.5" data-testid="register-password-hint">
                Mínimo 6 caracteres. Te recomendamos usar al menos una mayúscula y un número para mayor seguridad.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="Repite tu contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-soft"
                data-testid="register-confirm-password"
              />
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={acceptTerms}
                onCheckedChange={setAcceptTerms}
                className="mt-1"
                data-testid="register-terms"
              />
              <label 
                htmlFor="terms" 
                className="text-sm text-[hsl(var(--text-secondary))] cursor-pointer leading-relaxed"
              >
                He leído y acepto los{' '}
                <Link to="/terminos" target="_blank" className="text-[#c9a227] hover:underline">Términos y condiciones</Link>
                {' '}y la{' '}
                <Link to="/privacidad" target="_blank" className="text-[#c9a227] hover:underline">Política de privacidad</Link>.
              </label>
            </div>

            <Button 
              type="submit" 
              className="btn-gold w-full"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Creando cuenta...' : 'Crear cuenta'}
            </Button>
          </form>

          <p className="text-center text-[hsl(var(--text-secondary))] mt-8">
            ¿Ya tienes cuenta?{' '}
            <Link 
              to={redirectUrl ? `/login?redirect=${encodeURIComponent(redirectUrl)}` : '/login'} 
              className="text-[hsl(var(--accent))] hover:underline"
              data-testid="register-login-link"
            >
              Acceder
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
